import { ClientConfig } from '../../config';
import {
  DeletionReportSubmitFile,
  FileDeletionReportSubmitRequest,
  ResourceProcessor,
} from './resource';
import {
  BatchDownloadRequest,
  BatchDownloadProgress,
  BatchDownloadResult,
  BatchDownloadState,
  BatchDownloadErrorCode,
  FileDeleteResult,
  FileDeleteSource,
  FileTransferProgress,
  FileTransferResult,
  FileTransferSource,
  FileUploadResult,
  FileUploadSource,
} from './type';
import { getFileUploader, IFileUploader } from './uploader';

function triggerBrowserDownload(url: string, fileName?: string) {
  if (typeof window === 'undefined') return;
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName || '';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
}

interface IFileTransferService {
  transfer(
    fileTransferSource: FileTransferSource,
    progressReporter?: ((progress: FileTransferProgress) => void) | null
  ): Promise<FileTransferResult>;
}

class FileTransferService implements IFileTransferService {
  public constructor() {}

  /**
   * 간편 업로드 API - 브라우저 환경용
   * @example
   * const results = await FileTransferService.upload(files, 'USER_UPLOAD');
   */
  public static async upload(
    files: File[],
    sourceType: string = 'USER_UPLOAD',
    onProgress?: (progress: FileTransferProgress) => void
  ): Promise<FileUploadResult[]> {
    const uploadSource = new FileUploadSource(sourceType);
    files.forEach((file) => uploadSource.addBrowserData(file));

    const transferSource = new FileTransferSource(uploadSource);
    const service = new FileTransferService();
    const result = await service.transfer(transferSource, onProgress);

    return result.upload ?? [];
  }

  /**
   * 간편 삭제 API
   * @example
   * const results = await FileTransferService.delete(['file-id-1', 'file-id-2']);
   */
  public static async delete(fileIds: string[]): Promise<FileDeleteResult[]> {
    const deleteSource = new FileDeleteSource();
    fileIds.forEach((id) => deleteSource.add(id));

    const transferSource = new FileTransferSource(null, deleteSource);
    const service = new FileTransferService();
    const result = await service.transfer(transferSource);

    return result.delete ?? [];
  }

  private static readonly BATCH_DOWNLOAD_POLLING_INTERVAL_MS = 1000;

  /**
   * 여러 리소스를 ZIP으로 묶어 다운로드한다.
   * @param resourceIds - 다운로드할 리소스 ID 배열
   * @param zipFileName - 원하는 압축파일 이름 (선택)
   * @param onProgress - 진행 상태 콜백 (선택)
   * @param autoDownload - 완료 시 브라우저 자동 다운로드 (선택, 브라우저 환경에서만 동작)
   * @returns BatchDownloadResult (requestId, zipFileUri, ...)
   */
  public static async batchDownload(
    request: BatchDownloadRequest,
    autoDownload?: boolean,
    onProgress?: (progress: BatchDownloadProgress) => void
  ): Promise<BatchDownloadResult> {
    const processor = new ResourceProcessor();

    // 1. 배치 다운로드 요청 생성
    const createResponse = await processor.createBatchDownload({
      resourceIds: request.resourceIds,
      zipFileName: request.zipFileName,
    });
    if (!createResponse.isSuccess || !createResponse.data) {
      throw new Error('배치 다운로드 요청 생성에 실패했습니다.');
    }
    const { requestId, totalFileSize, fileCount } = createResponse.data;

    onProgress?.({ requestId, state: BatchDownloadState.ACCEPTED });

    // 2. 폴링으로 상태 확인
    return new Promise((resolve, reject) => {
      const poll = async () => {
        try {
          const statusResponse =
            await processor.getBatchDownloadStatus(requestId);
          if (!statusResponse.isSuccess || !statusResponse.data) {
            reject(new Error('배치 다운로드 상태 조회에 실패했습니다.'));
            return;
          }
          const status = statusResponse.data;

          onProgress?.({
            requestId,
            state: status.status as BatchDownloadProgress['state'],
          });

          if (status.status === 'COMPLETED') {
            const isBrowser =
              typeof window !== 'undefined' && window.sessionStorage !== null;
            const shouldAutoDownload = isBrowser && autoDownload !== false;
            if (shouldAutoDownload && status.zipFileUri) {
              triggerBrowserDownload(status.zipFileUri, request.zipFileName);
            }
            resolve({
              requestId,
              state: BatchDownloadState.COMPLETED,
              zipFileUri: status.zipFileUri!,
              fileCount: status.fileCount,
              totalFileSize: status.totalFileSize,
              errorCode: status.errorCode as BatchDownloadErrorCode | undefined,
            });
          } else if (status.status === 'FAILED') {
            resolve({
              requestId,
              state: BatchDownloadState.FAILED,
              zipFileUri: '',
              fileCount: 0,
              totalFileSize: 0,
              errorCode: status.errorCode as BatchDownloadErrorCode | undefined,
            });
          } else {
            setTimeout(
              poll,
              FileTransferService.BATCH_DOWNLOAD_POLLING_INTERVAL_MS
            );
          }
        } catch (err) {
          reject(err);
        }
      };
      poll();
    });
  }

  public async transfer(
    fileTransferSource: FileTransferSource,
    progressReporter: ((progress: FileTransferProgress) => void) | null = null
  ): Promise<FileTransferResult> {
    let fileTransferResult: FileTransferResult = {
      upload: null,
      delete: null,
    };

    //준비.
    if (progressReporter) {
      let progress: FileTransferProgress = {
        state: 'Preparing',
        fileName: '',
      };

      progressReporter(progress);
    }

    //업로드.
    if (fileTransferSource.uploadSource) {
      //업로드 진행상황을 보여줄 수 있는 '익명메소드'를 준비한다
      let uploadProgressReporter = (
        uploadProgress: FileTransferProgress
      ): void => {
        if (progressReporter) {
          let progress: FileTransferProgress = {
            state: 'Uploading',
            fileName: uploadProgress.fileName,
            progressRate: uploadProgress.progressRate,
            remainingTime: uploadProgress.remainingTime,
          };

          progressReporter(progress);
        }
      };

      // [웹브라우저] 또는 [파일시스템] 중, 어떤 환경에서 실행했는지에 따라 업로드 처리기를 따로 분리하였다_이렇게 한 이유는_웹브라우저에서 실행될 때, Node 런타임에서만 사용되는 개체가 포함된 스크립트를 읽어들이면(인터프리팅하면) 바로 실행을 중지하기 때문에_만약의 경우에 Node 런타임에서만 사용되는 개체가 포함된 스크립트를 실수로라도 실행 중에 인터프리팅하지않도록 막는다는 의미가 있다
      let fileUploader: IFileUploader = await getFileUploader();
      let fileUploadResults = await fileUploader.upload(
        fileTransferSource.uploadSource,
        uploadProgressReporter
      );
      fileTransferResult.upload = fileUploadResults;
    }

    //삭제.
    if (fileTransferSource.deleteSource) {
      //파일을 물리적으로 삭제하는 것이 아니기 때문에, 삭제는 [매우 빠르게] 진행된다_별도의 진행률 상태를 보여주지 않는다
      if (progressReporter) {
        let progress: FileTransferProgress = {
          state: 'Deleting',
          fileName: '',
        };

        progressReporter(progress);
      }

      let submitFiles: DeletionReportSubmitFile[] = [];
      fileTransferSource.deleteSource.items.forEach((fn) => {
        submitFiles.push({
          fileId: fn,
        });
      });
      let deletionReportSubmitRequest: FileDeletionReportSubmitRequest = {
        submitFiles: submitFiles,
      };

      //업로드(등록)과는 다르게 물리적으로 파일을 삭제하는 것이 아니라, 등록된 파일의 상태를 변경할 뿐이므로_[업로드처럼, 환경에 따른 처리기]를 만들지 않고, 바로 실행한다
      let resourceProcessor: ResourceProcessor = new ResourceProcessor();
      let reportSubmitResponse = await resourceProcessor.submitDeletionReport(
        deletionReportSubmitRequest
      );
      if (reportSubmitResponse.isSuccess && reportSubmitResponse.data) {
        let fileDeleteResults: FileDeleteResult[] = [];
        reportSubmitResponse.data.deletingFiles.forEach((df) => {
          fileDeleteResults.push({
            fileId: df.fileId,
            transferResult: 'succeed',
          });
        });

        fileTransferResult.delete = fileDeleteResults;
      }
    }

    //완료.
    if (progressReporter) {
      let progress: FileTransferProgress = {
        state: 'Completed',
        fileName: '',
      };

      progressReporter(progress);
    }

    return fileTransferResult;
  }

  public static getDownloadUri(fileId: string) {
    const config = ClientConfig.instance;
    return `${config.getResourceEndpoint()}/resource/files/${fileId}`;
  }
}

export { FileTransferService };
export type { IFileTransferService };
